/**
 * "Criar Orçamento ODOO" dialog — opens when a proposal transitions to
 * `ganho`. Pre-fills client data from the proposal so the comercial doesn't
 * re-type, plus a NIF/NIPC input that isn't in the lead schema.
 *
 * Flow on submit:
 *   form → POST actions/push_proposal_to_odoo → result panel
 * The result panel shows the Odoo order name + amount + any warnings
 * (lines that fell back to a generic SKU). The dialog stays open so the
 * comercial sees the outcome and can act on the warnings list.
 *
 * NIF format: Portuguese is 9 digits (individual or business). Light
 * validation only (length 9 + digits). Real check-digit validation lives
 * in the host's `nifpt` integration — not duplicated here.
 *
 * Idempotency: enforced HOST-side, not here. `push_proposal_to_odoo` sends an
 * `idempotencyKey` derived from (proposal, current odoo_order_id, payload hash),
 * and the host replays the previous result for 24h instead of creating a second
 * draft — so a double-click is safe. A deliberate "Re-enviar" still creates a
 * new order, because the back-link changed since the first push. On a replay the
 * handler says so explicitly ("já tinha sido enviado").
 */

import { useEffect, useState } from "react"
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ExternalLink,
  Loader2,
} from "lucide-react"
import type { AppShell } from "@/App"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { odooOrderLabel } from "@/lib/format"

export interface OdooClientPrefill {
  name: string
  email: string | null
  phone: string | null
  address: string | null
  city: string | null
  postal_code: string | null
  proposal_number?: string | null
}

export interface OdooQuoteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shell: AppShell | null
  /** UUID of the proposal — required to invoke push_proposal_to_odoo. */
  proposalId: string
  client: OdooClientPrefill
  /**
   * If set, the proposal has already been pushed to Odoo once. We surface
   * a banner with the existing order name + timestamp and label the
   * action as "Re-enviar". Re-pushing creates a NEW draft order in Odoo
   * (not an update) — by design until we wire `sale.order.write`.
   */
  existingOdoo?: {
    order_id: number
    order_name: string
    synced_at: string | null
  } | null
  /** Called after a successful push so the parent can reload the proposal. */
  onSynced?: () => void
}

interface OdooWarning {
  line_index: number
  original_ref: string
  used_fallback_variant_id: number
  reason: string
}

interface OdooPushResult {
  odoo_order_id: number
  odoo_order_name: string
  amount_total: number | null
  partner_id: number
  partner_created: boolean
  confirmed: boolean
  warnings: OdooWarning[]
}

const NIF_REGEX = /^\d{9}$/

/** Cliente que já existe no Odoo (odoo_partner_lookup). */
interface PartnerCandidate {
  id: number
  name: string
  vat: string | null
  email: string | null
  phone: string | null
  city: string | null
  is_company: boolean
  match: "nif" | "oportunidade" | "email" | "telefone"
}
const MATCH_LABEL: Record<PartnerCandidate["match"], string> = {
  nif: "mesmo NIF",
  oportunidade: "cliente da oportunidade",
  email: "mesmo email",
  telefone: "mesmo telefone",
}
/** NIF de entidade (empresa)? Particulares começam por 1, 2, 3 ou 45 — igual ao servidor (lib/odoo/partners.ts). */
const isCompanyNif = (nif: string) => NIF_REGEX.test(nif) && !(/^[123]/.test(nif) || nif.startsWith("45"))

export function OdooQuoteDialog({
  open,
  onOpenChange,
  shell,
  proposalId,
  client,
  existingOdoo,
  onSynced,
}: OdooQuoteDialogProps) {
  const [form, setForm] = useState({
    name: client.name,
    email: client.email ?? "",
    phone: client.phone ?? "",
    address: client.address ?? "",
    city: client.city ?? "",
    postal_code: client.postal_code ?? "",
    nif: "",
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<OdooPushResult | null>(null)
  const [resultMessage, setResultMessage] = useState<string | null>(null)
  // Clientes que já existem no Odoo (para não duplicar) e a escolha: um deles ou «novo».
  const [candidates, setCandidates] = useState<PartnerCandidate[] | null>(null)
  const [chosen, setChosen] = useState<number | "new">("new")
  const [looking, setLooking] = useState(false)

  // Re-prefill + reset state when dialog opens — caller could be navigating
  // between proposals without unmount.
  useEffect(() => {
    if (!open) return
    setForm({
      name: client.name,
      email: client.email ?? "",
      phone: client.phone ?? "",
      address: client.address ?? "",
      city: client.city ?? "",
      postal_code: client.postal_code ?? "",
      nif: "",
    })
    setError(null)
    setResult(null)
    setResultMessage(null)
  }, [
    open,
    client.name,
    client.email,
    client.phone,
    client.address,
    client.city,
    client.postal_code,
  ])

  const nifValid = NIF_REGEX.test(form.nif)
  const companyNif = isCompanyNif(form.nif)

  // Procura no Odoo quem já existe (NIF → cliente da oportunidade → email →
  // telefone) sempre que estes dados mudam. Escolha por omissão: o do mesmo
  // NIF; com NIF de empresa sem correspondência, um cliente novo (empresa);
  // sem NIF, o primeiro encontrado.
  useEffect(() => {
    if (!open || !shell || result) return
    const t = setTimeout(async () => {
      setLooking(true)
      try {
        const res = await shell.client.post<{ success: boolean; data?: { candidates: PartnerCandidate[] } }>(
          "actions/odoo_partner_lookup",
          { params: { proposal_id: proposalId, vat: nifValid ? form.nif : null, email: form.email.trim() || null, phone: form.phone.trim() || null } },
        )
        const list = res.success && res.data ? res.data.candidates : []
        setCandidates(list)
        const byNif = list.find((c) => c.match === "nif")
        setChosen(byNif ? byNif.id : nifValid && companyNif ? "new" : (list[0]?.id ?? "new"))
      } catch {
        setCandidates([])
        setChosen("new")
      } finally {
        setLooking(false)
      }
    }, 500)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shell, proposalId, form.nif, form.email, form.phone, result])

  // Cliente existente escolhido: não precisa de NIF aqui (está no Odoo).
  const canSubmit = form.name.trim().length > 0 && (chosen !== "new" || nifValid) && !submitting && !looking

  async function handleCreate() {
    if (!shell) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await shell.client.post<{
        success: boolean
        data?: OdooPushResult
        message?: string
        error?: string
      }>("actions/push_proposal_to_odoo", {
        params: {
          proposal_id: proposalId,
          confirm: false,
          // Always send the override — NIF isn't on the entity yet and the
          // user may have edited other fields too. The host's executor
          // prefers context.entityId for partner resolution but uses these
          // fields to backfill missing data (e.g. VAT).
          ...(chosen !== "new"
            ? { partner_id: chosen }
            : {
                client: {
                  name: form.name.trim(),
                  vat: form.nif,
                  email: form.email.trim() || null,
                  phone: form.phone.trim() || null,
                  street: form.address.trim() || null,
                  city: form.city.trim() || null,
                  zip: form.postal_code.trim() || null,
                  country_code: "PT",
                  // Empresa só com NIF de entidade (antes era sempre empresa).
                  is_company: companyNif,
                },
              }),
        },
      })
      if (!res.success || !res.data) {
        throw new Error(res.error || "Falha a criar orçamento no Odoo")
      }
      setResult(res.data)
      setResultMessage(res.message ?? null)
      shell.bridge.toast({
        variant: "success",
        description: `Orçamento ${odooOrderLabel(res.data.odoo_order_name, res.data.odoo_order_id)} criado no Odoo`,
      })
      // Let the parent reload to pick up the new odoo_order_id back-link
      // (so the header badge + "Re-enviar" labeling appear immediately).
      onSynced?.()
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido"
      setError(msg)
      shell.bridge.toast({ variant: "error", description: msg })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {result ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            ) : (
              <Building2 className="h-5 w-5 text-emerald-600" />
            )}
            {result ? "Orçamento criado em Odoo" : "Criar Orçamento ODOO"}
          </DialogTitle>
          <DialogDescription>
            {result ? (
              <>
                {resultMessage ?? `Orçamento ${odooOrderLabel(result.odoo_order_name, result.odoo_order_id)} em draft.`}
              </>
            ) : (
              <>
                Confirma os dados do cliente para gerar o orçamento em ODOO.
                {client.proposal_number ? (
                  <>
                    {" "}Proposta{" "}
                    <span className="font-mono">{client.proposal_number}</span>.
                  </>
                ) : null}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <ResultPanel result={result} />
        ) : (
          <>
            {existingOdoo ? <ExistingOdooBanner existing={existingOdoo} /> : null}
            <FormPanel
              form={form}
              setForm={setForm}
              nifValid={nifValid}
              error={error}
            />
            <PartnerChoice
              candidates={candidates}
              looking={looking}
              chosen={chosen}
              onChoose={setChosen}
              companyNif={companyNif}
              nifValid={nifValid}
            />
          </>
        )}

        <DialogFooter className="gap-2">
          {result ? (
            <Button onClick={() => onOpenChange(false)}>Fechar</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button onClick={handleCreate} disabled={!canSubmit}>
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Building2 className="h-4 w-4" />
                )}
                {existingOdoo
                  ? "Re-enviar (novo draft)"
                  : "Criar Orçamento ODOO"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Sub-components ──────────────────────────────────────────────────────

interface FormState {
  name: string
  email: string
  phone: string
  address: string
  city: string
  postal_code: string
  nif: string
}

function FormPanel({
  form,
  setForm,
  nifValid,
  error,
}: {
  form: FormState
  setForm: (next: FormState) => void
  nifValid: boolean
  error: string | null
}) {
  return (
    <div className="grid gap-3">
      {error ? (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <Field label="Nome / Empresa" required>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Field>

      <Field label="NIF / NIPC" required hint="9 dígitos">
        <Input
          inputMode="numeric"
          maxLength={9}
          value={form.nif}
          onChange={(e) =>
            setForm({ ...form, nif: e.target.value.replace(/\D/g, "") })
          }
          placeholder="123456789"
          className={
            form.nif.length > 0 && !nifValid
              ? "border-red-400 focus-visible:ring-red-400"
              : ""
          }
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Email">
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label="Telefone">
          <Input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Field>
      </div>

      <Field label="Morada">
        <Input
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Cidade">
          <Input
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
        </Field>
        <Field label="Código postal">
          <Input
            value={form.postal_code}
            onChange={(e) =>
              setForm({ ...form, postal_code: e.target.value })
            }
          />
        </Field>
      </div>
    </div>
  )
}

function ExistingOdooBanner({
  existing,
}: {
  existing: { order_id: number; order_name: string; synced_at: string | null }
}) {
  const when = existing.synced_at
    ? new Date(existing.synced_at).toLocaleString("pt-PT")
    : "—"
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs dark:border-amber-900 dark:bg-amber-950">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
      <div className="text-amber-800 dark:text-amber-300">
        Esta proposta já foi enviada:{" "}
        <span className="font-mono font-semibold">{odooOrderLabel(existing.order_name, existing.order_id)}</span>{" "}
        (#{existing.order_id}) em {when}.<br />
        Confirmar abaixo cria um <strong>novo draft</strong> no Odoo — o
        anterior fica como está.
      </div>
    </div>
  )
}

function ResultPanel({ result }: { result: OdooPushResult }) {
  return (
    <div className="space-y-3 text-sm">
      <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 dark:border-emerald-900 dark:bg-emerald-950">
        <div className="flex items-center justify-between">
          <span className="font-mono font-semibold">{odooOrderLabel(result.odoo_order_name, result.odoo_order_id)}</span>
          <span className="text-xs text-muted-foreground">
            #{result.odoo_order_id} · partner #{result.partner_id}
            {result.partner_created ? " (novo)" : ""}
          </span>
        </div>
        {result.amount_total != null ? (
          <div className="mt-1 text-xs text-muted-foreground">
            Total: {formatEur(result.amount_total)} ·{" "}
            {result.confirmed ? "Confirmado" : "Draft"}
          </div>
        ) : null}
      </div>

      {result.warnings.length > 0 ? (
        <div className="space-y-2">
          <Separator />
          <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="text-xs">
              {result.warnings.length} linha
              {result.warnings.length === 1 ? "" : "s"} registada
              {result.warnings.length === 1 ? "" : "s"} com SKU de fallback —
              cria{result.warnings.length === 1 ? "" : "m"}-se em Odoo + re-sync
              para orçamentos futuros usarem o produto directo.
            </div>
          </div>
          <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
            {result.warnings.map((w, i) => (
              <li
                key={`${w.line_index}-${i}`}
                className="rounded border border-amber-200 bg-amber-50 px-2 py-1 dark:border-amber-900 dark:bg-amber-950"
              >
                <span className="font-medium">Linha {w.line_index + 1}:</span>{" "}
                <span className="font-mono">{w.original_ref}</span>
                <span className="text-muted-foreground"> — {w.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          <ExternalLink className="mr-1 inline h-3 w-3" />
          Abre o Odoo para validar antes de confirmar.
        </p>
      )}
    </div>
  )
}

function formatEur(v: number | null | undefined): string {
  if (v == null) return "—"
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
  }).format(v)
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <Label>
        {label}
        {required ? <span className="ml-0.5 text-red-500">*</span> : null}
        {hint ? (
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {hint}
          </span>
        ) : null}
      </Label>
      {children}
    </div>
  )
}

/**
 * «Cliente no Odoo» — quem já existe (para não duplicar) e a opção de criar um
 * novo com os dados acima. Com NIF de empresa o novo é criado como empresa
 * (o orçamento pode ter sido feito em nome individual).
 */
function PartnerChoice({
  candidates,
  looking,
  chosen,
  onChoose,
  companyNif,
  nifValid,
}: {
  candidates: PartnerCandidate[] | null
  looking: boolean
  chosen: number | "new"
  onChoose: (v: number | "new") => void
  companyNif: boolean
  nifValid: boolean
}) {
  return (
    <div className="space-y-2 rounded-md border border-border p-3 text-sm">
      <p className="flex items-center gap-2 font-medium">
        Cliente no Odoo
        {looking && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </p>
      {candidates && candidates.length > 0 ? (
        <p className="text-xs text-muted-foreground">Já existe no Odoo — escolha o cliente para não criar outro.</p>
      ) : candidates ? (
        <p className="text-xs text-muted-foreground">Nenhum cliente no Odoo com este NIF, email ou telefone.</p>
      ) : null}
      {(candidates ?? []).map((c) => (
        <label key={c.id} className="flex cursor-pointer items-start gap-2">
          <input type="radio" name="odoo-partner" className="mt-1" checked={chosen === c.id} onChange={() => onChoose(c.id)} />
          <span>
            <span className="font-medium">{c.name}</span>
            {c.is_company ? <span className="ml-1 text-xs text-muted-foreground">(empresa)</span> : null}
            <span className="block text-xs text-muted-foreground">
              {[c.vat ? `NIF ${c.vat}` : null, c.email, c.phone, c.city].filter(Boolean).join(" · ")}
              {" — "}
              <span className="text-emerald-700">{MATCH_LABEL[c.match]}</span>
            </span>
          </span>
        </label>
      ))}
      <label className="flex cursor-pointer items-start gap-2">
        <input type="radio" name="odoo-partner" className="mt-1" checked={chosen === "new"} onChange={() => onChoose("new")} />
        <span>
          Criar cliente novo com os dados acima
          <span className="block text-xs text-muted-foreground">
            {nifValid
              ? companyNif
                ? "NIF de empresa — criado como empresa, com o nome indicado acima."
                : "Particular — com morada, telefone e email do Koortex."
              : "Indique o NIF para criar um cliente novo."}
          </span>
        </span>
      </label>
    </div>
  )
}
